import qs from 'qs';
import { createAction } from '@reduxjs/toolkit';
import { takeLatest, put, select } from 'redux-saga/effects';

import Notify from 'utils/Notify';
import http from 'app/services/httpService';
import { callWrapperSaga } from 'app/services/sagaService';
import apiEndpoints, { buildApiRoute } from 'routes/apiEndpoints';

import {
  getRolesInit,
  getRolesSuccess,
  getRolesFailure,
  getRoleInit,
  getRoleSuccess,
  getRoleFailure,
  addRolesInit,
  addRolesSuccess,
  addRolesFailure,
  updateRolesInit,
  updateRolesSuccess,
  updateRolesFailure,
  getMenusInit,
  getMenusSuccess,
  getMenusFailure,
  getMenuDetailsInit,
  getMenuDetailsSuccess,
  getMenuDetailsFailure,
  createRoleClaimSuccess,
  getRoleDropdownInit,
  getRoleDropdownSuccess,
  getRoleDropdownFailure,
  getUserRolesAndPermissionsInit,
  getUserRolesAndPermissionsSuccess,
  getUserRolesAndPermissionsFailure,
  getAuditLogsInit,
  getAuditLogsSuccess,
  getAuditLogsFailure,
} from './roleAndPermissionsSlice';

const getRoleAndPermission = createAction('rolesAndPermissions/get');
const createRolesAndPermissions = createAction('rolesAndPermissions/create');
const updateRolesAndPermissions = createAction('rolesAndPermissions/update');
const getRolesAndPermissions = createAction('rolesAndPermissions/list');
const deleteRoleAndPermission = createAction('rolesAndPermissions/delete');
const getRoleMenus = createAction('rolesAndPermissions/getMenus');
const getMenuDetails = createAction('rolesAndPermissions/getMenuDetails');
const createRoleClaim = createAction('rolesAndPermissions/createRoleClaim');
const getRoleDropdown = createAction('rolesAndPermissions/getRoleDropdown');
const getUserRolesAndPermissions = createAction('rolesAndPermissions/getUserRolesAndPermissions');
const getAuditLogs = createAction('rolesAndPermissions/getAuditLogs');

function* createRolesAndPermissionsSaga({ payload: data }) {
  yield put(addRolesInit());
  try {
    const response = yield callWrapperSaga(() =>
      http.post(apiEndpoints.createRoleandpermission, data),
    );
    yield put(addRolesSuccess(response.data));
    Notify.success('New Role created successfully.');
    const params = yield select((state) => state.rolesAndPermissions.index.params);
    yield put(getRolesAndPermissions(params));
  } catch (err) {
    yield put(addRolesFailure(err));
    Notify.error('Failed to create new Role.');
  }
}

function* updateRolesAndPermissionsSaga({ payload: data }) {
  try {
    yield put(updateRolesInit());
    const payload = {
      id: data.id,
      roleName: data.roleName,
      description: data.description,
      roleType: data.roleType,
      isActive: data.isActive,
      menuIds: data.menuIds ?? [],
    };
    const response = yield callWrapperSaga(() =>
      http.put(apiEndpoints.updateRoleandPermission, payload),
    );
    yield put(updateRolesSuccess(response.data));
    Notify.success('Role updated successfully.');
    const params = yield select((state) => state.rolesAndPermissions.index.params);
    yield put(getRolesAndPermissions(params));
  } catch (err) {
    yield put(updateRolesFailure(err));
    Notify.error('Failed to update Role.');
  }
}

function* getRoleAndPermissionSaga({ payload: roleId }) {
  try {
    yield put(getRoleInit());
    const endpoint = buildApiRoute(apiEndpoints.getRoleandPermission, roleId);
    const response = yield callWrapperSaga(() => http.get(endpoint));
    const data = response?.data?.data ?? response?.data;
    yield put(getRoleSuccess({ data }));
  } catch (err) {
    yield put(getRoleFailure(err));
  }
}

function* getRolesAndPermissionsSaga({ payload }) {
  yield put(getRolesInit());
  try {
    let endpoint = `${apiEndpoints.getRolesandPermissions}?${new URLSearchParams(payload)}`;
    let response = yield callWrapperSaga(() => http.get(endpoint));
    yield put(getRolesSuccess(response.data));
  } catch (err) {
    yield put(getRolesFailure(err));
  }
}

function* deleteRoleAndPermissionSaga({ payload: roleId }) {
  try {
    const endpoint = buildApiRoute(apiEndpoints.removeRoleandPermission, roleId);
    yield callWrapperSaga(() => http.delete(endpoint));
    Notify.success('Role deleted successfully.');

    const params = yield select((state) => state.rolesAndPermissions.index.params);
    yield put(getRolesAndPermissions(params));
  } catch (err) {
    Notify.error('Failed to delete Role.');
  }
}

function* getRoleMenusSaga({ payload: menuType }) {
  yield put(getMenusInit());
  try {
    const endpoint = buildApiRoute(apiEndpoints.getMenus, menuType || 'MENU');
    const response = yield callWrapperSaga(() => http.get(endpoint));
    const apiData = response?.data?.data || [];
    yield put(getMenusSuccess(apiData));
  } catch (err) {
    yield put(getMenusFailure(err));
    Notify.error('Failed to load menus.');
  }
}

function* getMenuDetailsSaga({ payload: roleId }) {
  yield put(getMenuDetailsInit());
  try {
    const endpoint = buildApiRoute(apiEndpoints.getMenuDetail, roleId);
    const response = yield callWrapperSaga(() => http.get(endpoint));
    const apiData = response?.data?.data || [];
    yield put(getMenuDetailsSuccess(apiData));
  } catch (err) {
    yield put(getMenuDetailsFailure(err));
    Notify.error('Failed to load menu details.');
  }
}

function* createRoleClaimSaga({ payload }) {
  try {
    yield callWrapperSaga(() => http.post(apiEndpoints.roleClaim, payload));
    Notify.success('Role claims updated successfully.');
    yield put(createRoleClaimSuccess());
  } catch (err) {
    Notify.error('Failed to update role claims.');
  }
}

function* getRoleDropdownSaga(action) {
  yield put(getRoleDropdownInit());
  try {
    const { isPartnerUserType } = action.payload;

    const response = yield callWrapperSaga(() =>
      http.get(apiEndpoints.roleDropdown, {
        params: { isPartnerUserType },
      }),
    );
    const apiData = response?.data?.data || [];
    yield put(getRoleDropdownSuccess(apiData));
  } catch (err) {
    yield put(getRoleDropdownFailure(err));
    Notify.error('Failed to load roles.');
  }
}

function* getUserRolesAndPermissionsSaga() {
  yield put(getUserRolesAndPermissionsInit());
  try {
    const response = yield callWrapperSaga(() => http.get(apiEndpoints.getUserRolesandPermissions));
    yield put(getUserRolesAndPermissionsSuccess(response.data));
  } catch (err) {
    yield put(getUserRolesAndPermissionsFailure(err));
  }
}

function* getAuditLogsSaga({ payload }) {
  yield put(getAuditLogsInit());
  try {
    const endpoint = buildApiRoute(apiEndpoints.getAuditLogs, payload?.key);
    const response = yield callWrapperSaga(() =>
      http.get(
        `${endpoint}?${new URLSearchParams({
          pageNumber: payload?.pageNumber,
          pageSize: payload?.pageSize,
        })}`,
      ),
    );
    yield put(getAuditLogsSuccess(response.data));
  } catch (err) {
    yield put(getAuditLogsFailure(err));
  }
}

function* rolesSaga() {
  yield takeLatest(getRolesAndPermissions, getRolesAndPermissionsSaga);
  yield takeLatest(getRoleAndPermission, getRoleAndPermissionSaga);
  yield takeLatest(createRolesAndPermissions, createRolesAndPermissionsSaga);
  yield takeLatest(updateRolesAndPermissions, updateRolesAndPermissionsSaga);
  yield takeLatest(deleteRoleAndPermission, deleteRoleAndPermissionSaga);
  yield takeLatest(getRoleMenus, getRoleMenusSaga);
  yield takeLatest(getMenuDetails, getMenuDetailsSaga);
  yield takeLatest(createRoleClaim, createRoleClaimSaga);
  yield takeLatest(getRoleDropdown, getRoleDropdownSaga);
  yield takeLatest(getUserRolesAndPermissions, getUserRolesAndPermissionsSaga);
  yield takeLatest(getAuditLogs, getAuditLogsSaga);
}

export default rolesSaga;

export {
  getRolesAndPermissions,
  getRoleAndPermission,
  createRolesAndPermissions,
  updateRolesAndPermissions,
  deleteRoleAndPermission,
  getRoleMenus,
  getMenuDetails,
  createRoleClaim,
  getRoleDropdown,
  getUserRolesAndPermissions,
  getAuditLogs,
};
